import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useUserStore } from '@/store/useUserStore';
import { buildRuntimeHashUrl } from '@/lib/appUrl';

export const baseUrl = import.meta.env.VITE_BASE_URL || '';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getPreviewUrl(id?: string | null): string {
  if (!id) {
    console.warn('[getPreviewUrl] Failed to build preview URL: missing project ID.');
    return '404';
  }

  const previewBaseUrl =
    import.meta.env.VITE_PREVIEW_URL || 'https://project-{id}.preview.shijieboya.cn';
  return previewBaseUrl.replace('{id}', id);
}

export function redirectToLogin() {
  window.location.replace(buildRuntimeHashUrl('/login'));
}

const normalizeApiMessageKey = (message: string) =>
  message.replace(/[\u951b\u5c7b\u20ac\u50a6\u7d12\u951b\u6db3\u7d30?\s]/g, '').trim();

export const translateApiMessage = (message?: string) => {
  const rawMessage = String(message || 'Operation failed').trim();
  const normalized = normalizeApiMessageKey(rawMessage);
  const includesAny = (...candidates: string[]) =>
    candidates.some((candidate) => {
      const normalizedCandidate = normalizeApiMessageKey(candidate);
      return rawMessage.includes(candidate) || normalized.includes(normalizedCandidate);
    });

  if (includesAny('资产ID不能为空')) {
    return 'Asset ID is required.';
  }

  if (
    includesAny(
      '资产类型不能为空',
      '\u74a7\u52ea\u9a87\u7eeb\u8bf2\u7037\u6d93\u5d88\u5158\u6d93\u8679\u2516',
    )
  ) {
    return 'Asset type is required.';
  }

  if (
    includesAny(
      'The specified field type is incompatible with the given campaign type',
      'The given field type is not supported to be added directly through asset links',
      '关联资产到广告系列失败',
      '\u934f\u5ba0\u4ec8\u74a7\u52ea\u9a87\u9352\u677f\u7b8d\u935b\u5a44\u90f4\u9352\u6940\u3051\u7490?',
    )
  ) {
    return 'The selected target does not support linking image assets.';
  }

  if (
    includesAny(
      '请选择账户列表中有权限的标准 Google Ads 账号',
      '\u7487\u70fd\u20ac\u590b\u5ae8\u7490\ufe3d\u57db\u9352\u6944\u3003\u6d93\ue15f\u6e41\u93c9\u51ae\u6aba\u9428\u52ec\u7223\u9351?Google Ads \u7490\ufe40\u5f7f',
      '\u7487\u70fd\u20ac\u590b\u5ae8\u7490\ufe3d\u57db\u9352\u6944\u3003\u6d93\ue15f\u6e41\u93c9\u51ae\u6aba\u9428\u52ec\u7223\u9351\u54b7oogleAds\u7490\ufe40\u5f7f',
    )
  ) {
    return 'Select a standard Google Ads account that you can access from the account list.';
  }

  if (
    includesAny(
      '不能直接绑定 MCC 经理账号',
      '\u6d93\u5d88\u5158\u9429\u5b58\u5e34\u7f01\u621d\u757e MCC \u7f01\u5fd5\u608a\u7490\ufe40\u5f7f',
      '\u6d93\u5d88\u5158\u9429\u5b58\u5e34\u7f01\u621d\u757eMCC\u7f01\u5fd5\u608a\u7490\ufe40\u5f7f',
    )
  ) {
    return 'An MCC manager account cannot be linked directly. Select a standard Google Ads account instead.';
  }

  if (
    includesAny(
      '草稿版本冲突',
      '请刷新最新草稿再修改',
      '\u947d\u590c\ue7c8\u9417\u581f\u6e70\u9350\u832c\u734a',
      '\u93c8\u20ac\u93c2\u62cc\u5d0f\u7ecb\u57ae\u5540\u6dc7\ue1bd\u657c',
    )
  ) {
    return 'Draft version conflict. Please refresh to the latest draft before editing.';
  }

  if (
    includesAny(
      'title不能超过60个字符',
      'Title不能超过60个字符',
      'title\u6d93\u5d88\u5158\u74d2\u5470\u7e4360\u6d93\ue044\u74e7\u7ed7?',
      'Title\u6d93\u5d88\u5158\u74d2\u5470\u7e4360\u6d93\ue044\u74e7\u7ed7?',
    )
  ) {
    return 'Title cannot exceed 60 characters.';
  }

  if (
    includesAny(
      'description不能超过160个字符',
      'Description不能超过160个字符',
      'description\u6d93\u5d88\u5158\u74d2\u5470\u7e43160\u6d93\ue044\u74e7\u7ed7?',
      'Description\u6d93\u5d88\u5158\u74d2\u5470\u7e43160\u6d93\ue044\u74e7\u7ed7?',
    )
  ) {
    return 'Description cannot exceed 160 characters.';
  }

  if (
    includesAny(
      '项目名称已存在请使用其他名称',
      '\u6924\u572d\u6d30\u935a\u5d87\u041e\u5bb8\u63d2\u74e8\u9366\u3128\ue1ec\u6d63\u8de8\u6564\u934f\u6735\u7cac\u935a\u5d87\u041e',
    )
  ) {
    return 'Project name already exists, please use a different name.';
  }

  if (includesAny('草稿未确认', '只有已确认状态的草稿才能发布')) {
    return 'Only confirmed drafts can be published.';
  }

  return rawMessage;
};

export type GoogleAdsOperationErrorContext = {
  resourceType?: 'ad' | 'keyword' | 'campaign' | 'adGroup' | 'resource';
  nextStatus?: string;
};

type GoogleAdsRequestErrorContext = {
  requestUrl?: string;
  requestData?: unknown;
};

const GOOGLE_ADS_ALLOWED_RESOURCE_LIMIT_MARKERS = [
  'The request would cause a limit on the number of allowed resources of this type to be exceeded',
];

export const isGoogleAdsAllowedResourceLimitMessage = (message?: string) => {
  const rawMessage = String(message || '').trim();
  if (!rawMessage) return false;

  const normalized = rawMessage.toLowerCase();
  return GOOGLE_ADS_ALLOWED_RESOURCE_LIMIT_MARKERS.some((marker) =>
    normalized.includes(marker.toLowerCase()),
  );
};

export const getGoogleAdsOperationErrorMessage = (
  message?: string,
  context: GoogleAdsOperationErrorContext = {},
) => {
  const translatedMessage = translateApiMessage(message);
  const normalizedStatus = String(context.nextStatus || '')
    .trim()
    .toUpperCase();

  if (
    context.resourceType === 'ad' &&
    normalizedStatus === 'ENABLED' &&
    (isGoogleAdsAllowedResourceLimitMessage(message) ||
      isGoogleAdsAllowedResourceLimitMessage(translatedMessage))
  ) {
    return 'Google Ads allows at most 3 enabled ads per ad group. Pause 1 ad before enabling another.';
  }

  return translatedMessage;
};

const readRequestRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  return typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
};

export const getContextualApiErrorMessage = (
  message?: string,
  context: GoogleAdsRequestErrorContext = {},
) => {
  const requestUrl = String(context.requestUrl || '')
    .trim()
    .toLowerCase();
  const requestData = readRequestRecord(context.requestData);

  if (requestUrl.includes('/googleads/ad/update')) {
    return getGoogleAdsOperationErrorMessage(message, {
      resourceType: 'ad',
      nextStatus: typeof requestData?.status === 'string' ? requestData.status : undefined,
    });
  }

  return translateApiMessage(message);
};

const HTML_RESPONSE_MARKERS = [
  '<!doctype',
  '<html',
  '<body',
  '<head',
  '<title',
  '</html',
  '</body>',
];
const INFRA_ERROR_MARKERS = [
  '404 not found',
  '502 bad gateway',
  '503 service unavailable',
  '504 gateway timeout',
];
const INFRA_SERVER_MARKERS = ['openresty', 'nginx', 'apache', 'cloudflare', 'server at'];

export function isHtmlLikeResponse(message?: string): boolean {
  const rawMessage = String(message || '').trim();
  if (!rawMessage) return false;

  const normalized = rawMessage.toLowerCase();
  if (HTML_RESPONSE_MARKERS.some((marker) => normalized.includes(marker))) {
    return true;
  }

  const tagMatches = rawMessage.match(/<\/?[a-z][\w:-]*(?:\s[^<>]*)?>/gi);
  return (tagMatches?.length ?? 0) >= 3;
}

export function normalizeUnexpectedResponseMessage(
  message?: string,
  fallback = 'The request failed before a valid response was returned. Please try again.',
): string {
  const rawMessage = String(message || '').trim();
  if (!rawMessage) return fallback;

  const normalized = rawMessage.toLowerCase();
  const looksLikeInfraErrorPage =
    INFRA_ERROR_MARKERS.some((marker) => normalized.includes(marker)) &&
    INFRA_SERVER_MARKERS.some((marker) => normalized.includes(marker));

  if (isHtmlLikeResponse(rawMessage) || looksLikeInfraErrorPage) {
    return fallback;
  }

  return translateApiMessage(rawMessage);
}

export function handleApiError(
  code: number | string,
  errorMessage?: string,
  skipAuthCheck?: boolean,
): string {
  let message = 'Network error, please check your connection';

  switch (Number(code)) {
    case 401:
      if (!skipAuthCheck) {
        useUserStore.getState().clearAuth();
        redirectToLogin();
        message = 'Session expired, please log in again';
      }
      break;
    case 403:
      message = 'Access denied, insufficient permissions';
      break;
    case 500:
      message = 'Internal server error, please try again later';
      break;
    default:
      message = translateApiMessage(errorMessage) || `Request failed: ${code}`;
  }

  return message;
}
